'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/context/LanguageContext';

interface SignupFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  accountType: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useLanguage();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignupFormData>({
    defaultValues: { accountType: 'customer' }
  });

  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setLoading(true);
    // TODO: POST /api/auth/register with user data
    await new Promise((r) => setTimeout(r, 1800));
    setLoading(false);
    toast.success('Account created! Please check your email to verify your account.');
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display font-bold text-2xl text-foreground mb-1">{t('auth.createYourAccount')}</h1>
        <p className="text-muted-foreground text-sm">
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Account type */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t('auth.accountType')}</label>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: 'customer', label: t('auth.customer'), desc: t('auth.customerDesc'), icon: 'UserIcon' },
              { value: 'corporate', label: t('auth.corporate'), desc: t('auth.corporateDesc'), icon: 'BuildingOfficeIcon' }
            ].map((type) =>
              <label
                key={`acct-type-${type.value}`}
                className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-all ${
                watch('accountType') === type.value ?
                'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                <input
                  {...register('accountType')}
                  type="radio"
                  value={type.value}
                  className="sr-only" />
                <div className={`p-1.5 rounded-lg ${watch('accountType') === type.value ? 'bg-primary/10' : 'bg-muted'}`}>
                  <Icon name={type.icon as 'UserIcon'} size={16} className={watch('accountType') === type.value ? 'text-primary' : 'text-muted-foreground'} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{type.label}</p>
                  <p className="text-xs text-muted-foreground">{type.desc}</p>
                </div>
              </label>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('auth.firstName')} <span className="text-destructive">*</span>
            </label>
            <input
              {...register('firstName', { required: t('auth.required') })}
              type="text"
              placeholder="First name"
              className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
              errors.firstName ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`} />

            {errors.firstName && <p className="text-destructive text-xs mt-1">{errors.firstName.message}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {t('auth.lastName')} <span className="text-destructive">*</span>
            </label>
            <input
              {...register('lastName', { required: t('auth.required') })}
              type="text"
              placeholder="Last name"
              className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
              errors.lastName ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`} />

            {errors.lastName && <p className="text-destructive text-xs mt-1">{errors.lastName.message}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('auth.emailAddress')} <span className="text-destructive">*</span>
          </label>
          <input
            {...register('email', {
              required: t('auth.required'),
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' }
            })}
            type="email"
            placeholder="your@email.com"
            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
            errors.email ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`}
          />

          {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">{t('auth.phone')}</label>
          <input
            {...register('phone')}
            type="tel"
            placeholder="+1 (555) 000-0000"
            className="w-full px-3 py-2.5 border border-border rounded-lg text-sm outline-none focus:border-primary transition-colors" />

        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('auth.password')} <span className="text-destructive">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-1">{t('auth.passwordHint')}</p>
          <div className="relative">
            <input
              {...register('password', {
                required: t('auth.required'),
                minLength: { value: 8, message: 'At least 8 characters' },
                pattern: { value: /(?=.*[0-9])(?=.*[!@#$%^&*])/, message: 'Must include a number and symbol' }
              })}
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password"
              className={`w-full px-3 py-2.5 pr-10 border rounded-lg text-sm outline-none transition-colors ${
              errors.password ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`}
            />

            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? 'Hide password' : 'Show password'}>

              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && <p className="text-destructive text-xs mt-1">{errors.password.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            {t('auth.confirmPassword')} <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <input
              {...register('confirmPassword', {
                required: t('auth.required'),
                validate: (val) => val === password || 'Passwords do not match'
              })}
              type={showConfirm ? 'text' : 'password'}
              placeholder="Confirm your password"
              className={`w-full px-3 py-2.5 pr-10 border rounded-lg text-sm outline-none transition-colors ${
              errors.confirmPassword ? 'border-destructive bg-red-50' : 'border-border focus:border-primary'}`}
            />

            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}>

              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.confirmPassword && <p className="text-destructive text-xs mt-1">{errors.confirmPassword.message}</p>}
        </div>

        <div className="flex items-start gap-2">
          <input
            {...register('agreeTerms', { required: t('auth.required') })}
            type="checkbox"
            id="agreeTerms"
            className="w-4 h-4 accent-primary rounded mt-0.5 flex-shrink-0" />

          <label htmlFor="agreeTerms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
            {t('auth.agreeTerms')}
          </label>
        </div>
        {errors.agreeTerms && <p className="text-destructive text-xs">{errors.agreeTerms.message}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-primary text-white font-semibold text-sm rounded-lg hover:bg-primary-light transition-all duration-150 active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
          style={{ minHeight: '44px' }}>

          {loading ?
          <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('auth.creatingAccount')}
            </> :

          t('auth.createButton')
          }
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-4">
        {t('auth.alreadyHaveAccount')}{' '}
        <button onClick={onSwitchToLogin} className="text-primary font-medium hover:underline">
          {t('auth.signInLink')}
        </button>
      </p>
    </div>);

}